// HLD Wizard types — mirrors api/internal/models/question_hld_wizard.go

// ==================== Content (sent to users) ====================

export interface HLDWizardContent {
  scenario: string;
  decisions: DecisionContent[];
  max_points: number;
}

export interface DecisionContent {
  id: string;
  title: string;
  points: number;
  context: string;
  question: string;
  // Tagged union — exactly one non-null
  choose_justify?: ChooseJustifySpec;
  choose_checklist?: ChooseChecklistSpec;
  choose_adaptive_justify?: ChooseAdaptiveJustifySpec;
  ordering?: OrderingSpec;
  choose_signals_scenario?: ChooseSignalsScenarioSpec;
}

export interface DecisionOption {
  id: string;
  label: string;
  description: string;
}

// --- choose_justify ---

export interface ChooseJustifySpec {
  options: DecisionOption[];
  justifications: JustificationConfig;
  downside: DownsideConfig;
}

export interface JustificationConfig {
  adaptive: boolean;
  max_selections: number;
  sets: Record<string, JustificationItem[]>;
}

export interface JustificationItem {
  id: string;
  text: string;
}

export interface DownsideConfig {
  adaptive: boolean;
  prompts: Record<string, string>;
  max_chars: number;
}

// --- choose_checklist ---

export interface ChooseChecklistSpec {
  options: DecisionOption[];
  checklist: ChecklistConfig;
}

export interface ChecklistConfig {
  adaptive: boolean;
  question: string;
  sets: Record<string, ChecklistItem[]>;
}

export interface ChecklistItem {
  id: string;
  text: string;
}

// --- choose_adaptive_justify ---

export interface ChooseAdaptiveJustifySpec {
  options: DecisionOption[];
  secondary: SecondaryConfig;
  tertiary?: TertiaryConfig;
}

export interface SecondaryConfig {
  question: string;
  options: SecondaryOption[];
  max_chars: number;
}

export interface SecondaryOption {
  id: string;
  label: string;
  justify_prompt: string;
}

export interface TertiaryConfig {
  question: string;
  options: DecisionOption[];
}

// --- ordering ---

export interface OrderingSpec {
  items: OrderingItem[];
  followup: FollowupConfig;
  followup_justify: FollowupJustifyConfig;
}

export interface OrderingItem {
  id: string;
  text: string;
}

export interface FollowupConfig {
  question: string;
  options: FollowupOption[];
}

export interface FollowupOption {
  id: string;
  text: string;
}

export interface FollowupJustifyConfig {
  placeholder: string;
  max_chars: number;
}

// --- choose_signals_scenario ---

export interface ChooseSignalsScenarioSpec {
  options: DecisionOption[];
  signals: SignalsConfig;
  scenario: ScenarioQConfig;
}

export interface SignalsConfig {
  question: string;
  max_selections: number;
  items: SignalItem[];
}

export interface SignalItem {
  id: string;
  text: string;
}

export interface ScenarioQConfig {
  question: string;
  options: ScenarioOption[];
}

export interface ScenarioOption {
  id: string;
  text: string;
  description: string;
}

// ==================== Answer Types ====================

export interface HLDWizardResponse {
  decisions: Record<string, DecisionAnswer>;
}

export interface DecisionAnswer {
  choose_justify?: ChooseJustifyAnswer;
  choose_checklist?: ChooseChecklistAnswer;
  choose_adaptive_justify?: ChooseAdaptiveJustifyAnswer;
  ordering?: OrderingAnswer;
  choose_signals_scenario?: ChooseSignalsScenarioAnswer;
}

export interface ChooseJustifyAnswer {
  option_id: string;
  justification_ids: string[];
  downside_text: string;
}

export interface ChooseChecklistAnswer {
  option_id: string;
  checked_ids: string[];
}

export interface ChooseAdaptiveJustifyAnswer {
  option_id: string;
  secondary_option_id: string;
  secondary_justify_text: string;
  tertiary_option_id?: string;
}

export interface OrderingAnswer {
  order: string[];
  initial_order: string[];
  followup_option_id: string;
  followup_justify_text: string;
}

export interface ChooseSignalsScenarioAnswer {
  option_id: string;
  signal_ids: string[];
  scenario_option_id: string;
}

// ==================== Hidden Answer (review mode) ====================

export interface HLDWizardHiddenAnswer {
  decisions: Record<string, DecisionGrading>;
}

export interface DecisionGrading {
  choose_justify?: ChooseJustifyGrading;
  choose_checklist?: ChooseChecklistGrading;
  choose_adaptive_justify?: ChooseAdaptiveJustifyGrading;
  ordering?: OrderingGrading;
  choose_signals_scenario?: ChooseSignalsScenarioGrading;
}

export interface TextGradingConfig {
  reference_answer: string;
  grading_context: string;
}

export interface ChooseJustifyGrading {
  option_tiers: Record<string, number>;
  justification_validity: Record<string, string[]>;
  downside_grading: TextGradingConfig;
}

export interface ChooseChecklistGrading {
  option_tiers: Record<string, number>;
  checklist_correctness: Record<string, Record<string, boolean>>;
}

export interface ChooseAdaptiveJustifyGrading {
  option_tiers: Record<string, number>;
  secondary_tiers: Record<string, number>;
  tertiary_tiers?: Record<string, number>;
  justify_grading: TextGradingConfig;
}

export interface OrderingGrading {
  correct_positions: Record<string, number>;
  followup_tiers: Record<string, number>;
  followup_justify_grading: TextGradingConfig;
}

export interface ChooseSignalsScenarioGrading {
  option_tiers: Record<string, number>;
  signal_tiers: Record<string, number>;
  scenario_correctness: Record<string, ScenarioCorrectGiven | null>;
}

export interface ScenarioCorrectGiven {
  depends_on_decision: string;
  correct_when: string[];
}

// ==================== Feedback (parsed from Result.Feedback JSON) ====================

export interface WizardFeedback {
  summary: string;
  total_score: number;
  max_score: number;
  decisions: Record<string, DecisionScoreFeedback>;
}

export interface DecisionScoreFeedback {
  title: string;
  score: number;
  max_score: number;
  feedback: string;
  components: Record<string, ComponentScore>;
}

export interface ComponentScore {
  score: number;
  max_score: number;
}

// ==================== Helpers ====================

export type DecisionType =
  | "choose_justify"
  | "choose_checklist"
  | "choose_adaptive_justify"
  | "ordering"
  | "choose_signals_scenario";

export function getDecisionType(d: DecisionContent): DecisionType {
  if (d.choose_justify) return "choose_justify";
  if (d.choose_checklist) return "choose_checklist";
  if (d.choose_adaptive_justify) return "choose_adaptive_justify";
  if (d.ordering) return "ordering";
  if (d.choose_signals_scenario) return "choose_signals_scenario";
  throw new Error(`Unknown decision type for decision ${d.id}`);
}

export function getDecisionOptionId(answer: DecisionAnswer): string | undefined {
  if (answer.choose_justify) return answer.choose_justify.option_id;
  if (answer.choose_checklist) return answer.choose_checklist.option_id;
  if (answer.choose_adaptive_justify) return answer.choose_adaptive_justify.option_id;
  if (answer.choose_signals_scenario) return answer.choose_signals_scenario.option_id;
  return undefined;
}

export function isHLDWizardContent(
  content: unknown,
): content is HLDWizardContent {
  if (typeof content !== "object" || content === null) return false;
  const c = content as Record<string, unknown>;
  return "scenario" in c && "decisions" in c && Array.isArray(c.decisions);
}

export function isHLDWizardUIAnswer(
  answer: unknown,
): answer is HLDWizardResponse {
  if (typeof answer !== "object" || answer === null) return false;
  return "decisions" in answer && typeof (answer as Record<string, unknown>).decisions === "object";
}

export function isHLDWizardHiddenAnswer(
  hidden: unknown,
): hidden is HLDWizardHiddenAnswer {
  if (typeof hidden !== "object" || hidden === null) return false;
  return "decisions" in hidden && typeof (hidden as Record<string, unknown>).decisions === "object";
}
